import {Footer as FooterType, Header as HeaderType} from '@/(payload)/payload-types'
import LinkWithArrow                                from '@/components/buttons/LinkWithArrow'
import useFooterAnimation                           from '@/components/global/Footer.animation'
import GradientCanvas                               from '@/components/global/GradientCanvas'
import LocalisationAndTime                          from '@/components/global/LocalisationAndTime'
import {usePageContext}                             from '@/context/PageContext'
import {RichText}                                   from '@payloadcms/richtext-lexical/react'
import cn                                           from 'classnames'
import Link                                         from 'next/link'
import {usePathname}                                from 'next/navigation'
import {useState}                                   from 'react'

import styles from './Footer.module.sass'

type Props = {
	data: FooterType, header: HeaderType
}

const Footer = ({
	data,
	header,
}: Props) => {
	const {lang}   = usePageContext()
	const pathname = usePathname()

	const [hovering, setHovering] = useState(false)

	const {footerRef} = useFooterAnimation()

	return (<footer
		className={cn(styles.footer, {
			[styles['footer--homepage']]: pathname.split('/').length === 2,
			[styles['footer--contact']]:  pathname.includes('contact'),
		})}
		ref={footerRef}
	>
		{!pathname.includes('contact') && <PreFooter data={data.prefooter}/>}

		<div className={styles.footer__content}>
			{/* Wrapper with clip-path */}
			<div className={styles.footer__wrapper}>
				{/* Top section */}
				<div className={styles.top_section}>
					{/* Location */}
					<Link
						href={data.maps ?? ''}
						rel={'nofollow'}
						className={styles.location}
					>
						{data.address?.map((line, index) => (<span key={index}>{line.line}</span>))}
					</Link>

					{/* Links */}
					<div className={styles.links}>
						{/* Projects */}
						{data.pages?.map((page, index) => (<LinkWithArrow
							key={index}
							href={`/${lang}${page.link.url}`}
						>{page.link.label}</LinkWithArrow>))}
					</div>

					{/* Socials */}
					<div className={styles.socials}>
						{data.networks?.map((network, index) => (<LinkWithArrow
							key={index}
							href={network.link.url}
						>{network.link.label}</LinkWithArrow>))}
					</div>

					{/* Contact */}
					<div className={styles.contact}>
						{/* Title */}
						{data.contact.title && <RichText
							className={styles.contact__title}
							data={data.contact.title}
						/>}

						{/* Mail */}
						<LinkWithArrow href={'mailto:' + (data.contact.link.url ?? '')}>{data.contact.link.url}</LinkWithArrow>
					</div>

					{/* Right side */}
					<LocalisationAndTime
						showContactLink={false}
						data={header}
					/>
				</div>

				{/* Bottom section */}
				<div className={styles.bottom_section}>
					{/* Mentions */}
					<div className={styles.mentions}>
						{/* Gax */}
						<div className={styles.gax}>
							Designed with

							<svg
								width="14"
								height="10"
								viewBox="0 0 14 10"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_557_4251)">
									<path
										d="M2.62467 1.66662C2.07395 1.66662 1.75006 1.32751 1.75006 0.833311C1.75006 0.309006 2.10622 0 2.62467 0C3.17579 0 3.49982 0.339632 3.49982 0.833311C3.49982 1.32699 3.14366 1.66662 2.62467 1.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M4.37497 1.66662C3.82426 1.66662 3.50037 1.32751 3.50037 0.833311C3.50037 0.309006 3.85653 0 4.37497 0C4.9261 0 5.25013 0.339632 5.25013 0.833311C5.25013 1.32699 4.89397 1.66662 4.37497 1.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M9.62491 1.66662C9.0742 1.66662 8.75031 1.32751 8.75031 0.833311C8.75031 0.309006 9.10646 0 9.62491 0C10.176 0 10.5001 0.339632 10.5001 0.833311C10.5001 1.32699 10.1439 1.66662 9.62491 1.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M11.375 1.66662C10.8243 1.66662 10.5004 1.32751 10.5004 0.833311C10.5004 0.309006 10.8565 0 11.375 0C11.9261 0 12.2501 0.339632 12.2501 0.833311C12.2501 1.32699 11.894 1.66662 11.375 1.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M0.874607 3.33362C0.323893 3.33362 0 2.9945 0 2.5003C0 1.976 0.356159 1.66699 0.874607 1.66699C1.42573 1.66699 1.74976 2.00662 1.74976 2.5003C1.74976 2.99398 1.3936 3.33362 0.874607 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M2.62467 3.33362C2.07395 3.33362 1.75006 2.9945 1.75006 2.5003C1.75006 1.976 2.10622 1.66699 2.62467 1.66699C3.17579 1.66699 3.49982 2.00662 3.49982 2.5003C3.49982 2.99398 3.14366 3.33362 2.62467 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M4.37497 3.33362C3.82426 3.33362 3.50037 2.9945 3.50037 2.5003C3.50037 1.976 3.85653 1.66699 4.37497 1.66699C4.9261 1.66699 5.25013 2.00662 5.25013 2.5003C5.25013 2.99398 4.89397 3.33362 4.37497 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M6.12485 3.33362C5.57414 3.33362 5.25024 2.9945 5.25024 2.5003C5.25024 1.976 5.6064 1.66699 6.12485 1.66699C6.67598 1.66699 7.00001 2.00662 7.00001 2.5003C7.00001 2.99398 6.64385 3.33362 6.12485 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M7.87485 3.33362C7.32414 3.33362 7.00024 2.9945 7.00024 2.5003C7.00024 1.976 7.3564 1.66699 7.87485 1.66699C8.42598 1.66699 8.75001 2.00662 8.75001 2.5003C8.75001 2.99398 8.39385 3.33362 7.87485 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M9.62491 3.33362C9.0742 3.33362 8.75031 2.9945 8.75031 2.5003C8.75031 1.976 9.10646 1.66699 9.62491 1.66699C10.176 1.66699 10.5001 2.00662 10.5001 2.5003C10.5001 2.99398 10.1439 3.33362 9.62491 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M11.375 3.33362C10.8243 3.33362 10.5004 2.9945 10.5004 2.5003C10.5004 1.976 10.8565 1.66699 11.375 1.66699C11.9261 1.66699 12.2501 2.00662 12.2501 2.5003C12.2501 2.99398 11.894 3.33362 11.375 3.33362Z"
										fill="#E66363"
									/>
									<path
										d="M13.125 3.33362C12.5743 3.33362 12.2504 2.9945 12.2504 2.5003C12.2504 1.976 12.6065 1.66699 13.125 1.66699C13.6761 1.66699 14.0001 2.00662 14.0001 2.5003C14.0001 2.99398 13.644 3.33362 13.125 3.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M0.874607 4.99963C0.323893 4.99963 0 4.66052 0 4.16632C0 3.64201 0.356159 3.33301 0.874607 3.33301C1.42573 3.33301 1.74976 3.67264 1.74976 4.16632C1.74976 4.66 1.3936 4.99963 0.874607 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M2.62467 4.99963C2.07395 4.99963 1.75006 4.66052 1.75006 4.16632C1.75006 3.64201 2.10622 3.33301 2.62467 3.33301C3.17579 3.33301 3.49982 3.67264 3.49982 4.16632C3.49982 4.66 3.14366 4.99963 2.62467 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M4.37497 4.99963C3.82426 4.99963 3.50037 4.66052 3.50037 4.16632C3.50037 3.64201 3.85653 3.33301 4.37497 3.33301C4.9261 3.33301 5.25013 3.67264 5.25013 4.16632C5.25013 4.66 4.89397 4.99963 4.37497 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M6.12485 4.99963C5.57414 4.99963 5.25024 4.66052 5.25024 4.16632C5.25024 3.64201 5.6064 3.33301 6.12485 3.33301C6.67598 3.33301 7.00001 3.67264 7.00001 4.16632C7.00001 4.66 6.64385 4.99963 6.12485 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M7.87485 4.99963C7.32414 4.99963 7.00024 4.66052 7.00024 4.16632C7.00024 3.64201 7.3564 3.33301 7.87485 3.33301C8.42598 3.33301 8.75001 3.67264 8.75001 4.16632C8.75001 4.66 8.39385 4.99963 7.87485 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M9.62491 4.99963C9.0742 4.99963 8.75031 4.66052 8.75031 4.16632C8.75031 3.64201 9.10646 3.33301 9.62491 3.33301C10.176 3.33301 10.5001 3.67264 10.5001 4.16632C10.5001 4.66 10.1439 4.99963 9.62491 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M11.375 4.99963C10.8243 4.99963 10.5004 4.66052 10.5004 4.16632C10.5004 3.64201 10.8565 3.33301 11.375 3.33301C11.9261 3.33301 12.2501 3.67264 12.2501 4.16632C12.2501 4.66 11.894 4.99963 11.375 4.99963Z"
										fill="#E66363"
									/>
									<path
										d="M13.125 4.99963C12.5743 4.99963 12.2504 4.66052 12.2504 4.16632C12.2504 3.64201 12.6065 3.33301 13.125 3.33301C13.6761 3.33301 14.0001 3.67264 14.0001 4.16632C14.0001 4.66 13.644 4.99963 13.125 4.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M2.62467 6.66662C2.07395 6.66662 1.75006 6.32751 1.75006 5.83331C1.75006 5.30901 2.10622 5 2.62467 5C3.17579 5 3.49982 5.33963 3.49982 5.83331C3.49982 6.32699 3.14366 6.66662 2.62467 6.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M4.37497 6.66662C3.82426 6.66662 3.50037 6.32751 3.50037 5.83331C3.50037 5.30901 3.85653 5 4.37497 5C4.9261 5 5.25013 5.33963 5.25013 5.83331C5.25013 6.32699 4.89397 6.66662 4.37497 6.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M6.12485 6.66662C5.57414 6.66662 5.25024 6.32751 5.25024 5.83331C5.25024 5.30901 5.6064 5 6.12485 5C6.67598 5 7.00001 5.33963 7.00001 5.83331C7.00001 6.32699 6.64385 6.66662 6.12485 6.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M7.87485 6.66662C7.32414 6.66662 7.00024 6.32751 7.00024 5.83331C7.00024 5.30901 7.3564 5 7.87485 5C8.42598 5 8.75001 5.33963 8.75001 5.83331C8.75001 6.32699 8.39385 6.66662 7.87485 6.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M9.62491 6.66662C9.0742 6.66662 8.75031 6.32751 8.75031 5.83331C8.75031 5.30901 9.10646 5 9.62491 5C10.176 5 10.5001 5.33963 10.5001 5.83331C10.5001 6.32699 10.1439 6.66662 9.62491 6.66662Z"
										fill="#E66363"
									/>
									<path
										d="M11.375 6.66662C10.8243 6.66662 10.5004 6.32751 10.5004 5.83331C10.5004 5.30901 10.8565 5 11.375 5C11.9261 5 12.2501 5.33963 12.2501 5.83331C12.2501 6.32699 11.894 6.66662 11.375 6.66662Z"
										fill="#AC1414"
									/>
									<path
										d="M4.37497 8.33362C3.82426 8.33362 3.50037 7.9945 3.50037 7.5003C3.50037 6.976 3.85653 6.66699 4.37497 6.66699C4.9261 6.66699 5.25013 7.00662 5.25013 7.5003C5.25013 7.99398 4.89397 8.33362 4.37497 8.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M6.12485 8.33362C5.57414 8.33362 5.25024 7.9945 5.25024 7.5003C5.25024 6.976 5.6064 6.66699 6.12485 6.66699C6.67598 6.66699 7.00001 7.00662 7.00001 7.5003C7.00001 7.99398 6.64385 8.33362 6.12485 8.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M7.87485 8.33362C7.32414 8.33362 7.00024 7.9945 7.00024 7.5003C7.00024 6.976 7.3564 6.66699 7.87485 6.66699C8.42598 6.66699 8.75001 7.00662 8.75001 7.5003C8.75001 7.99398 8.39385 8.33362 7.87485 8.33362Z"
										fill="#E66363"
									/>
									<path
										d="M9.62491 8.33362C9.0742 8.33362 8.75031 7.9945 8.75031 7.5003C8.75031 6.976 9.10646 6.66699 9.62491 6.66699C10.176 6.66699 10.5001 7.00662 10.5001 7.5003C10.5001 7.99398 10.1439 8.33362 9.62491 8.33362Z"
										fill="#AC1414"
									/>
									<path
										d="M6.12485 9.99963C5.57414 9.99963 5.25024 9.66052 5.25024 9.16632C5.25024 8.64201 5.6064 8.33301 6.12485 8.33301C6.67598 8.33301 7.00001 8.67264 7.00001 9.16632C7.00001 9.66 6.64385 9.99963 6.12485 9.99963Z"
										fill="#AC1414"
									/>
									<path
										d="M7.87485 9.99963C7.32414 9.99963 7.00024 9.66052 7.00024 9.16632C7.00024 8.64201 7.3564 8.33301 7.87485 8.33301C8.42598 8.33301 8.75001 8.67264 8.75001 9.16632C8.75001 9.66 8.39385 9.99963 7.87485 9.99963Z"
										fill="#AC1414"
									/>
								</g>
								<defs>
									<clipPath id="clip0_557_4251">
										<rect
											width="14"
											height="10"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>

							by
							<Link
								href={'https://gax.design'}
								className={styles.mentions__link}
							>GAX Creative Studio</Link>
						</div>

						{/* Ziben */}
						<div className={styles.ziben}>
							Developed by <Link
							href={'https://www.ziben.fr'}
							className={styles.mentions__link}
						>Ziben</Link>
						</div>
					</div>
				</div>
			</div>

			<GradientCanvas
				className={styles.gradient_background}
				hovering={hovering}
				onMouseEnter={() => setHovering(true)}
				onMouseLeave={() => setHovering(false)}
			/>
		</div>
	</footer>)
}

export default Footer

type PreFooterProps = {
	data: FooterType['prefooter']
}

const PreFooter = ({data}: PreFooterProps) => {
	const {
		      lang,
		      preFooterRef,
	      } = usePageContext()

	return (<div
		className={styles['prefooter']}
		ref={preFooterRef}
	>
		<h2 className={styles['prefooter__title']}>{data.title}</h2>

		<Link
			className={styles['prefooter__cta']}
			href={`/${lang}${data.link.url}`}
			data-cursor-pointer
		>
			{data.link.label}
		</Link>
	</div>)
}
